import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { procoreApi } from "apis/procore/procoreApi";
import { type RootState } from "app";
import { selectIsProcoreMobileDesign, selectMinimapTilingEnabled } from "slices/explorer";
import { AsyncState, AsyncStatus } from "types/misc";

import { createSheetProjection } from "./sheetProjection";
// Imported from the leaf module rather than the `tiledDrawing` barrel: the
// barrel pulls in `downloadTiledDrawingData`, which imports this slice back.
import { hasAvailableTileSet } from "./tiledDrawing/tileAvailability";
import type {
    Drawing,
    DrawingAB,
    DrawingImage,
    DrawingPlacement,
    DrawingPreview,
    Level,
    ModelAB,
    NavigationPin,
    Uuid,
} from "./types";

/** Passed to the host `onAddSheetsForNavigation` callback to distinguish between
 *  the two empty-state situations shown in MiniMapWeb:
 *  - `'no-mapped'`        – no sheets have calibration data at all
 *  - `'not-used-for-nav'` – sheets are calibrated but none have use_for_2d_nav
 */
export type MiniMapEmptyStateSituation = "no-mapped" | "not-used-for-nav";

export type MiniMapState = {
    levels: Level[]; // Kept sorted by elevation (ascending)
    drawings: Record<Uuid, Drawing>;
    modelABs: Record<Uuid, ModelAB>;
    drawingABs: Record<Uuid, DrawingAB>;
    currentLevelId: Uuid | null;
    currentDrawingId: Uuid | null;
    tempDrawingId: Uuid | null; // Temporary drawing selection (e.g., while browsing in LargeMapWeb)
    isLargeMapMobileOpen: boolean;
    /** Total number of sheets that have sheet_dimensions (calibrated), regardless of use_for_2d_nav. */
    mappedSheetCount: number;
    /** True once the first `getMiniMapData` response has been received (success only —
     *  a rejected fetch leaves this false so the safe area and Settings toggle stay
     *  inactive when the component renders nothing). */
    hasMiniMapData: boolean;
    /** True while `getMiniMapData` is in-flight.  Lets the safe area and Settings
     *  toggle treat the loading shimmer the same as the canvas or empty-state card,
     *  rather than waiting for the first successful response before reserving space. */
    isMiniMapFetching: boolean;
    /** Mirror of `useMiniMapData().isLoading` — the whole metadata pipeline, both
     *  the revisions hop and the `getMiniMapData` hop. Set imperatively by that
     *  hook rather than by a matcher: the revisions query is shared across the
     *  app, so its pending state alone doesn't imply the mini map is waiting on
     *  it, and `getMiniMapData.matchPending` starts a tick after the widget
     *  begins painting its shimmer. Folded into `selectMiniMapFetching` so
     *  Settings / `useViewportSafeArea` see exactly the loading window the
     *  widget does. */
    isWidgetLoading: boolean;
    /** Mirror of `useMiniMapData().isError`. `MiniMapWeb` returns null on a
     *  terminal fetch failure, but `hasMiniMapData` / `levels` survive a failed
     *  *refetch*, so without this a stale success would keep the corner reserved
     *  for a widget rendering nothing. */
    isWidgetError: boolean;
    /** Host-provided callback invoked when the user taps "Add 2D Navigation" in the empty-state UI.
     *  Non-serializable; stored outside Redux serializability checks via the miniMap ignoredPath. */
    onAddSheetsForNavigation: ((situation: MiniMapEmptyStateSituation) => void) | null;
    navigationPin: NavigationPin | null;
};

export type MiniMapPayload = Omit<
    MiniMapState,
    | "tempDrawingId"
    | "isLargeMapMobileOpen"
    | "navigationPin"
    | "onAddSheetsForNavigation"
    | "hasMiniMapData"
    | "isMiniMapFetching"
    | "isWidgetLoading"
    | "isWidgetError"
>;

const initialState: MiniMapState = {
    levels: [],
    drawings: {},
    modelABs: {},
    drawingABs: {},
    currentLevelId: null,
    currentDrawingId: null,
    tempDrawingId: null,
    isLargeMapMobileOpen: false,
    mappedSheetCount: 0,
    hasMiniMapData: false,
    isMiniMapFetching: false,
    isWidgetLoading: false,
    isWidgetError: false,
    onAddSheetsForNavigation: null,
    navigationPin: null,
};

export const miniMapSlice = createSlice({
    name: "miniMap",
    initialState,
    reducers: {
        setCurrentLevel: (state, action: PayloadAction<Uuid>) => {
            state.currentLevelId = action.payload;
        },
        setCurrentDrawing: (state, action: PayloadAction<Uuid>) => {
            state.currentDrawingId = action.payload;
        },
        // Explicit user navigation to a sheet: select it and hold that selection
        // until the camera has flown into it. `expiresAt` comes from the caller
        // so the reducer stays pure.
        navigateToDrawing: (state, action: PayloadAction<{ drawingId: Uuid; expiresAt: number }>) => {
            state.currentDrawingId = action.payload.drawingId;
            state.navigationPin = { drawingId: action.payload.drawingId, expiresAt: action.payload.expiresAt };
        },
        clearNavigationPin: (state) => {
            state.navigationPin = null;
        },
        // Full-resolution tiles state (large map). Kept separate from the preview
        // so a tile failure/upgrade never disturbs the preview the mini maps show.
        setDrawingData: (state, action: PayloadAction<{ drawingId: Uuid; asyncState: AsyncState<DrawingImage> }>) => {
            const drawing = state.drawings[action.payload.drawingId];
            if (drawing) {
                drawing.drawingData.asyncState = action.payload.asyncState;
            }
        },
        // Low-resolution preview state (mini maps + large map loading UI).
        setDrawingPreview: (
            state,
            action: PayloadAction<{ drawingId: Uuid; asyncState: AsyncState<DrawingPreview> }>,
        ) => {
            const drawing = state.drawings[action.payload.drawingId];
            if (drawing) {
                drawing.drawingData.previewAsyncState = action.payload.asyncState;
            }
        },
        setTempDrawingId: (state, action: PayloadAction<Uuid | null>) => {
            state.tempDrawingId = action.payload;
        },
        setLargeMapMobileOpen: (state, action: PayloadAction<boolean>) => {
            state.isLargeMapMobileOpen = action.payload;
        },
        setEmptyStateHandlers: (
            state,
            action: PayloadAction<{
                onAddSheetsForNavigation: ((situation: MiniMapEmptyStateSituation) => void) | null;
            }>,
        ) => {
            state.onAddSheetsForNavigation = action.payload.onAddSheetsForNavigation;
        },
        clearEmptyStateHandlers: (state) => {
            state.onAddSheetsForNavigation = null;
        },
        /** Mirrors the widget's own loading/error predicate into the store so
         *  `selectIsMiniMapWidgetShown` is the single source of truth for
         *  `MiniMapWeb`, Settings and `useViewportSafeArea`. Dispatched from
         *  `useMiniMapData`. */
        setMiniMapWidgetStatus: (state, action: PayloadAction<{ loading: boolean; error: boolean }>) => {
            state.isWidgetLoading = action.payload.loading;
            state.isWidgetError = action.payload.error;
        },
    },
    extraReducers: (builder) => {
        // Mirror `getMiniMapData` cache into slice state so existing selectors
        // (`selectMiniMapLevels`, `selectCurrentDrawing`, ...) keep working
        // unchanged. Levels are sorted by elevation; the payload's current
        // level/drawing act as defaults for a first fetch only. Per-drawing
        // image fetching is owned by `useMiniMapData` / component camera
        // handlers via `setDrawingData`.
        //
        // Drawings, modelABs and drawingABs are shallow-cloned because the
        // RTK Query cache reference is frozen; later `setDrawingData` calls
        // mutate `state.drawings[id].drawingData.asyncState`, which would
        // throw if the object came directly from the (frozen) cache.
        builder.addMatcher(procoreApi.endpoints.getMiniMapData.matchPending, (state) => {
            state.isMiniMapFetching = true;
        });
        builder.addMatcher(procoreApi.endpoints.getMiniMapData.matchRejected, (state) => {
            state.isMiniMapFetching = false;
        });
        builder.addMatcher(procoreApi.endpoints.getMiniMapData.matchFulfilled, (state, action) => {
            const { levels, drawings, modelABs, drawingABs, currentLevelId, currentDrawingId, mappedSheetCount } =
                action.payload;
            state.isMiniMapFetching = false;
            state.hasMiniMapData = true;
            state.levels = [...levels].sort((a, b) => a.elevation - b.elevation);
            state.mappedSheetCount = mappedSheetCount;

            const drawingsCopy: Record<Uuid, Drawing> = {};
            for (const id of Object.keys(drawings)) {
                const d = drawings[id];
                drawingsCopy[id] = { ...d, drawingData: { ...d.drawingData } };
            }
            state.drawings = drawingsCopy;
            state.modelABs = { ...modelABs };
            state.drawingABs = { ...drawingABs };

            // A refetch (`forceMiniMapRefresh`, cache invalidation, remount)
            // must not throw the user back to the default sheet, so the current
            // selection is kept whenever the new payload still contains it. The
            // drawing's async state is reset by the copy above, so it still
            // re-downloads on a forced refresh.
            const retained =
                state.currentDrawingId !== null && drawingsCopy[state.currentDrawingId] ? state.currentDrawingId : null;
            if (retained) {
                state.currentLevelId =
                    state.levels.find((level) => level.drawingIds.includes(retained))?.id ?? currentLevelId;
            } else {
                state.currentLevelId = currentLevelId;
                state.currentDrawingId = currentDrawingId;
            }

            if (state.navigationPin && !drawingsCopy[state.navigationPin.drawingId]) {
                state.navigationPin = null;
            }
        });
    },
});

export const {
    setCurrentLevel,
    setCurrentDrawing,
    navigateToDrawing,
    clearNavigationPin,
    setDrawingData,
    setDrawingPreview,
    setTempDrawingId,
    setLargeMapMobileOpen,
    setEmptyStateHandlers,
    clearEmptyStateHandlers,
    setMiniMapWidgetStatus,
} = miniMapSlice.actions;

/**
 * Every level and drawing the scene has, renderable or not.
 *
 * Camera-driven sheet resolution runs on these rather than the filtered
 * views below, and that is load-bearing (BM-13878): `findDrawingIdByElevation`
 * snaps to the nearest level at or below the camera, so hiding an
 * un-renderable floor from it would resolve a user standing on that floor to
 * the floor *below* and draw their position indicator on the wrong sheet.
 * Resolving to the hidden sheet instead makes `selectCurrentDrawing` return
 * `null`, which renders nothing — the intended outcome.
 */
export const selectMiniMapLevels = (state: RootState) => state.miniMap.levels;
export const selectMiniMapHasData = (state: RootState) => state.miniMap.hasMiniMapData;
// Combines the `getMiniMapData` in-flight flag with the mirrored widget
// predicate, which also covers the revisions hop that precedes it, so callers
// see the same loading window as the shimmer in `MiniMapWeb`.
export const selectMiniMapFetching = (state: RootState) =>
    state.miniMap.isMiniMapFetching || state.miniMap.isWidgetLoading;
export const selectMiniMapWidgetError = (state: RootState) => state.miniMap.isWidgetError;

const selectAllMiniMapDrawings = (state: RootState) => state.miniMap.drawings;

/**
 * Whether sheets this platform can't render must be hidden rather than drawn
 * from their PDF (BM-13878). True only on the Procore mobile design with the
 * host's tiling flag on — the one configuration with no PDF route to fall
 * back to.
 */
const selectHideUnrenderableSheets = (state: RootState) =>
    selectIsProcoreMobileDesign(state) && selectMinimapTilingEnabled(state);

/**
 * The drawings this platform can actually render.
 *
 * On mobile the tiled route is the only route, so a sheet is dropped when it
 * has no ready tile set, or when fetching that tile set has failed. Either way
 * it resolves to `null` through `selectCurrentDrawing` / `selectTempDrawing`
 * and the map renders nothing — no spinner, no error badge. That is
 * deliberate (RFD00134 thread): field users can neither map a sheet nor
 * re-run a failed import, so a sheet they can do nothing about should not
 * advertise itself. On web every drawing renders from its PDF, so the state
 * record is passed straight through — same reference, no allocation.
 */
export const selectMiniMapDrawings = createSelector(
    [selectAllMiniMapDrawings, selectHideUnrenderableSheets],
    (drawings, hideUnrenderableSheets) =>
        hideUnrenderableSheets
            ? Object.fromEntries(Object.entries(drawings).filter(([, drawing]) => isRenderableOnTiles(drawing)))
            : drawings,
);

/** A tile set that exists and hasn't already failed to load this session. */
function isRenderableOnTiles(drawing: Drawing): boolean {
    return (
        hasAvailableTileSet(drawing.drawingData) && drawing.drawingData.previewAsyncState.status !== AsyncStatus.Error
    );
}

/**
 * Levels for the level pickers, with unrenderable sheets pruned out and
 * levels left empty dropped entirely.
 *
 * Only the pickers use this — offering a sheet that opens to a blank panel
 * is a dead end. Camera resolution deliberately keeps using
 * {@link selectMiniMapLevels}; see its doc for why.
 */
export const selectMiniMapPickerLevels = createSelector(
    [selectMiniMapLevels, selectMiniMapDrawings, selectHideUnrenderableSheets],
    (levels, drawings, hideUnrenderableSheets) =>
        hideUnrenderableSheets
            ? levels
                  .map((level) => ({ ...level, drawingIds: level.drawingIds.filter((id) => drawings[id]) }))
                  .filter((level) => level.drawingIds.length > 0)
            : levels,
);
export const selectMiniMapModelABs = (state: RootState) => state.miniMap.modelABs;
export const selectMiniMapDrawingABs = (state: RootState) => state.miniMap.drawingABs;
export const selectCurrentLevel = (state: RootState) => state.miniMap.currentLevelId;
/**
 * The raw current sheet id, renderable or not.
 *
 * Camera resolution needs this rather than `selectCurrentDrawing?.id`: a
 * hidden sheet resolves to `null` there, which would throw away the "keep the
 * sheet you're on until you leave its area" hysteresis in
 * `findDrawingIdForCamera` exactly while the user is standing on it.
 */
export const selectCurrentDrawingId = (state: RootState) => state.miniMap.currentDrawingId;
export const selectTempDrawingId = (state: RootState) => state.miniMap.tempDrawingId;
export const selectIsLargeMapMobileOpen = (state: RootState) => state.miniMap.isLargeMapMobileOpen;
export const selectMiniMapMappedSheetCount = (state: RootState) => state.miniMap.mappedSheetCount;
export const selectMiniMapOnAddSheetsForNavigation = (state: RootState) => state.miniMap.onAddSheetsForNavigation;
export const selectNavigationPin = (state: RootState) => state.miniMap.navigationPin;

/** Sheets the user can page through in the large map — picker levels, for the same reason. */
export const selectMiniMapDrawingIds = createSelector([selectMiniMapPickerLevels], (levels) =>
    levels.flatMap((level) => level.drawingIds),
);

// Every drawing that can be matched against a camera position, joined with its
// level elevation and its resolved calibration. Drawings missing either half of
// the mapping are dropped — without both there is no way to place a camera on
// them. Memoized so the projections are rebuilt only when the mini map data
// changes, not on every camera update.
//
// Built from the unfiltered drawings on purpose (BM-13878): a placement for an
// un-renderable sheet is what lets the camera resolve to that sheet and then
// render nothing, instead of falling through to whatever sheet is below it.
export const selectDrawingPlacements = createSelector(
    [selectMiniMapLevels, selectAllMiniMapDrawings, selectMiniMapModelABs, selectMiniMapDrawingABs],
    (levels, drawings, modelABs, drawingABs): DrawingPlacement[] =>
        levels.flatMap((level) =>
            level.drawingIds.flatMap((drawingId) => {
                const drawing = drawings[drawingId];
                if (!drawing) {
                    return [];
                }
                const modelAB = modelABs[drawing.modelABId];
                const drawingAB = drawingABs[drawing.drawingABId];
                if (!modelAB || !drawingAB) {
                    return [];
                }
                return [
                    {
                        drawingId,
                        levelId: level.id,
                        elevation: level.elevation,
                        projection: createSheetProjection(modelAB, drawingAB),
                    },
                ];
            }),
        ),
);

// Helper function to get a drawing with its modelAB and drawingAB
const getDrawingWithABs = (
    drawingId: Uuid | null,
    drawings: Record<Uuid, Drawing>,
    modelABs: Record<Uuid, ModelAB>,
    drawingABs: Record<Uuid, DrawingAB>,
) => {
    if (!drawingId) {
        return null;
    }
    const drawing = drawings[drawingId];
    if (!drawing) {
        return null;
    }
    const modelAB = modelABs[drawing.modelABId];
    const drawingAB = drawingABs[drawing.drawingABId];
    return {
        ...drawing,
        modelAB: modelAB || null,
        drawingAB: drawingAB || null,
    };
};

export const selectCurrentDrawing = createSelector(
    [selectCurrentDrawingId, selectMiniMapDrawings, selectMiniMapModelABs, selectMiniMapDrawingABs],
    (currentDrawingId, drawings, modelABs, drawingABs) =>
        getDrawingWithABs(currentDrawingId, drawings, modelABs, drawingABs),
);

/**
 * Single source of truth for "is a mini map widget actually on screen", shared by
 * `MiniMapWeb`, the Settings visibility toggle and `useViewportSafeArea` so the
 * three cannot drift apart again. Does *not* account for the user having hidden
 * the widget (`selectIsMiniMapVisible`) — callers that own that flag AND it in
 * themselves.
 *
 * The two platforms render different components and so have different rules:
 *
 * - **Desktop** (`MiniMapWeb`): canvas/shimmer while loading or once there are nav
 *   sheets, otherwise the empty-state card — which is itself gated on a host
 *   `onAddSheetsForNavigation` handler (`if (!onAddSheetsForNavigation) return
 *   null`). A successful fetch alone is therefore not enough: standalone builds
 *   and hosts that pass no handler render nothing for an unmapped project.
 * - **Mobile** (`MiniMapMobile`): mounts the canvas only when a current drawing is
 *   set (`currentDrawing && <MiniMap … />`, both compact and normal mode) and has
 *   no empty-state card at all, so `hasMiniMapData` says nothing about whether the
 *   canvas is on screen.
 *
 * `isMobile` is passed in rather than read from state because the platform flag
 * lives outside this slice. Reselect 5's default `weakMapMemoize` caches per
 * argument, so the desktop and mobile call sites don't evict each other.
 */
export const selectIsMiniMapWidgetShown = createSelector(
    [
        selectMiniMapFetching,
        selectMiniMapWidgetError,
        selectMiniMapLevels,
        selectMiniMapHasData,
        selectMiniMapOnAddSheetsForNavigation,
        selectCurrentDrawing,
        (_state: RootState, isMobile: boolean) => isMobile,
    ],
    (fetching, isError, levels, hasData, onAddSheetsForNavigation, currentDrawing, isMobile) => {
        if (isMobile) {
            return currentDrawing != null;
        }
        if (isError) {
            return false;
        }
        return fetching || levels.length > 0 || (hasData && onAddSheetsForNavigation != null);
    },
);

export const selectTempDrawing = createSelector(
    [
        selectTempDrawingId,
        selectCurrentDrawingId,
        selectMiniMapDrawings,
        selectMiniMapModelABs,
        selectMiniMapDrawingABs,
    ],
    (tempDrawingId, currentDrawingId, drawings, modelABs, drawingABs) =>
        getDrawingWithABs(tempDrawingId ?? currentDrawingId, drawings, modelABs, drawingABs),
);

export const selectTempLevel = createSelector(
    [selectTempDrawingId, selectCurrentDrawingId, selectMiniMapLevels],
    (tempDrawingId, currentDrawingId, levels) => {
        const drawingId = tempDrawingId ?? currentDrawingId;
        if (!drawingId) {
            return null;
        }
        return levels.find((level) => level.drawingIds.includes(drawingId)) ?? null;
    },
);

export const miniMapReducer = miniMapSlice.reducer;
